/**
 * Conditional visibility for provider field specs (GET /api/connections/providers).
 *
 * A field may carry `showIf: { field: '<other field>', in: ['value', …] }`, which makes it
 * apply only when that sibling holds one of the listed values — how the MCP provider asks
 * "which auth scheme?" first and then shows only that scheme's credentials.
 *
 * This mirrors `fieldApplies` in services/connections/providers.js and the two MUST agree:
 * the server drops non-applicable fields on write and skips their `required`, so a form
 * that showed a field the server considers inapplicable would silently discard what the
 * user typed, and one that hid a field the server still requires would 400 on save.
 *
 * The scope a showIf resolves against is config ∪ credentials — a plaintext config field
 * gates secret ones, so the form knows which secrets to ask for without ever decrypting.
 */

/** @param {object} field - one field spec @param {object} scope - config ∪ credentials */
export function fieldApplies(field, scope) {
  const cond = field && field.showIf;
  if (!cond) return true;
  return (cond.in || []).includes(scope ? scope[cond.field] : undefined);
}

/** The subset of `fields` a form should render for `scope`. Safe on a missing spec. */
export function visibleFields(fields, scope) {
  return (fields || []).filter((f) => fieldApplies(f, scope));
}

/**
 * Values for fields that no longer apply — what a form must clear when the gating field
 * changes, so a secret typed under the previous auth type is never submitted. Returns the
 * keys to drop rather than a new object, so callers can clear config and credentials
 * independently while resolving both against one shared scope.
 */
export function staleFieldNames(fields, scope) {
  return (fields || []).filter((f) => !fieldApplies(f, scope)).map((f) => f.name);
}
